import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  confirmExtraction,
  dispatchExtraction,
  getComponent,
  getExtraction,
  linkComponent,
  listGlobalComponents,
  listProjectComponents,
  unlinkComponent,
} from "../api/components";
import type { ComponentType } from "../types/components";

export interface GlobalComponentQuery {
  type?: ComponentType;
  manufacturer?: string;
  q?: string;
  linked?: boolean;
  limit?: number;
  offset?: number;
}

export const componentKeys = {
  globalList: (q: GlobalComponentQuery) =>
    ["components", "global", q] as const,
  detail: (id: string) => ["components", "detail", id] as const,
  projectLinked: (projectId: string, type: ComponentType | undefined) =>
    ["components", "project", projectId, type ?? "all"] as const,
  extraction: (id: string) => ["extractions", id] as const,
};

export function useGlobalComponents(query: GlobalComponentQuery) {
  return useQuery({
    queryKey: componentKeys.globalList(query),
    queryFn: () => listGlobalComponents(query),
    staleTime: 30_000,
  });
}

export function useComponent(id: string | undefined) {
  return useQuery({
    queryKey: id ? componentKeys.detail(id) : ["components", "detail", "none"],
    queryFn: () => (id ? getComponent(id) : Promise.reject(new Error("no id"))),
    enabled: !!id,
  });
}

export function useProjectComponents(
  projectId: string | undefined,
  type?: ComponentType,
) {
  return useQuery({
    queryKey: projectId
      ? componentKeys.projectLinked(projectId, type)
      : ["components", "project", "none", type ?? "all"],
    queryFn: () =>
      projectId
        ? listProjectComponents(projectId, type)
        : Promise.reject(new Error("no projectId")),
    enabled: !!projectId,
  });
}

export function useLinkComponent(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (componentId: string) => linkComponent(projectId, componentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["components"] });
    },
  });
}

export function useUnlinkComponent(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (componentId: string) => unlinkComponent(projectId, componentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["components"] });
    },
  });
}

export function useDispatchExtraction(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (layerId: string) => dispatchExtraction(projectId, layerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["layers", projectId] });
    },
  });
}

export function useExtraction(extractionId: string | undefined) {
  return useQuery({
    queryKey: extractionId
      ? componentKeys.extraction(extractionId)
      : ["extractions", "none"],
    queryFn: () =>
      extractionId
        ? getExtraction(extractionId)
        : Promise.reject(new Error("no id")),
    enabled: !!extractionId,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 1000;
      if (
        data.status === "succeeded" ||
        data.status === "failed" ||
        data.status === "duplicate"
      ) {
        return false;
      }
      return 1500;
    },
  });
}

export function useConfirmExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => confirmExtraction(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["components"] });
      void qc.invalidateQueries({ queryKey: ["extractions"] });
    },
  });
}
